'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/lib/cartStore';
import { Badge } from '@/components/ui/badge';
import { Button, type buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';

type CartButtonProps = {
  storeId: string;
  cartStoreId?: string;
  locale: string;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  className?: string;
};

export function CartButton({
  storeId,
  cartStoreId,
  locale,
  variant = 'ghost',
  className,
}: CartButtonProps) {
  const itemCount = useCartStore((state) => state.getItemCount());
  const setStoreId = useCartStore((state) => state.setStoreId);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (cartStoreId) {
      setStoreId(cartStoreId);
    }
  }, [cartStoreId, setStoreId]);

  return (
    <div className="relative">
      <Button variant={variant} size="icon" className={cn(className)} asChild>
        <Link href={`/${locale}/${storeId}/cart`} aria-label="Shopping cart">
          <ShoppingCart className="h-[1.2rem] w-[1.2rem]" />
        </Link>
      </Button>
      {mounted && itemCount > 0 && (
        <Badge
          className="bg-primary dark:bg-red-500 absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 text-xs font-bold pointer-events-none"
        >
          {itemCount}
        </Badge>
      )}
    </div>
  );
}
